import { useMutation } from '@apollo/client';

import { cacheUtils, eluveAdminHasuraContext } from '@eluve/apollo-client';
import { useToast } from '@eluve/components';
import { DbEnums } from '@eluve/graphql-types';

import { getAdminTenantsSummaryFilterQuery } from '../../../components/query';
import {
  AddUserToTenantMutation,
  TenantUsersFragment,
  UserFragment,
} from '../operations';

export const useAddUserToTenant = (
  tenantId: string,
  onUpdate: () => Promise<void> = () => Promise.resolve(),
) => {
  const toast = useToast();
  let isOptimistic = true;
  const [addUserToTenant] = useMutation(AddUserToTenantMutation, {
    context: eluveAdminHasuraContext,
    refetchQueries: [getAdminTenantsSummaryFilterQuery],
    onError: () => toast.error('Failed to add user to organization'),
    optimisticResponse: (vars) => {
      const user = cacheUtils.readFragment({
        fragment: UserFragment,
        key: { id: vars.userId },
      });

      return {
        __typename: 'Mutation' as const,
        insertTenantUsersOne: {
          __typename: 'TenantUsers' as const,
          tenantId: vars.tenantId,
          userId: vars.userId,
          role: vars.role,
          isExcluded: vars.isExcluded ?? false,
          createdAt: new Date().toISOString(),
          lastAccessedAt: new Date().toISOString(),
          // New members default to CREATED (the tenant_users column default).
          lifecycleStatus: DbEnums.TenantUserLifecycleStatus.CREATED,
          user: {
            ...user,
            __typename: 'Users' as const,
            id: vars.userId,
          },
        },
      };
    },

    update(_, { data }) {
      if (isOptimistic) {
        isOptimistic = false;
        return;
      }
      onUpdate().then(() => {
        cacheUtils.updateFragment(
          {
            fragment: TenantUsersFragment,
            key: { id: tenantId },
          },
          (existingData) => {
            if (!data?.insertTenantUsersOne) return existingData;

            if (
              existingData?.tenantUsers?.some(
                (tu) => tu.userId === data.insertTenantUsersOne?.userId,
              )
            ) {
              return existingData;
            }

            return {
              tenantUsers: [
                ...(existingData?.tenantUsers ?? []),
                data.insertTenantUsersOne,
              ],
            };
          },
        );
      });
    },
  });

  return addUserToTenant;
};
